import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { GetWarehousesApiParameter } from "@insite/client-framework/Services/WarehouseService";
import applyChanges from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/ApplyChanges";
import loadWarehouses from "@insite/client-framework/Store/Components/FindLocationModal/Handlers/LoadWarehouses";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getWarehousesDataView } from "@insite/client-framework/Store/Data/Warehouses/WarehousesSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import MapPin from "@insite/mobius/Icons/MapPin";
import Popover, { PopoverPresentationProps } from "@insite/mobius/Popover";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LoadingOverlay, { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import AddressInfoCondensedDisplay, { AddressInfoCondensedDisplayStyles } from "@store/Overrides/Components/AddressInfoCondensedDisplay";
import { getStoreSession, getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import updateWarehouse from "@store/Store/Context/Warehouses/Handlers/UpdateWarehouse";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { getLoadingOverlayProps } from "@store/theme/common/props/loadingOverlay";
import { COMMON, MENU, TYPOGRAPHY } from "@store/theme/design";
import breakpointMediaQueries, { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import Color from "color";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps { }

const mapStateToProps = (state: StoreApplicationState) => {
    const session = getStoreSession(state);
    const settings = getStoreSettingsCollection(state);

    const warehousesParameter: GetWarehousesApiParameter = {
        search: "",
        latitude: 0,
        longitude: 0,
        pageSize: 1000,
        onlyPickupWarehouses: true,
    };

    return {
        rememberMe: state.context.session.rememberMe,
        isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
        warehousesDataView: getWarehousesDataView(state, warehousesParameter),
        warehousesParameter,
        fulfillmentMethod: session.fulfillmentMethod,
        currentWarehouseName:
            (session.fulfillmentMethod === FulfillmentMethod.PickUp
                ? session.pickUpWarehouse?.name
                : session.warehouse.name),
        currentWarehouseDescription:
            (session.fulfillmentMethod === FulfillmentMethod.PickUp
                ? session.pickUpWarehouse?.description
                : session.warehouse.description),
        currentBillTo: getCurrentBillToState(state).value,
        disallowedShippingBranches: settings.poolcorpSettings.disallowedShippingBranches || '',
    };
};

const mapDispatchToProps = {
    loadWarehouses,
    applyChanges: makeHandlerChainAwaitable(applyChanges),
    updatePickUpWarehouse,
    updateWarehouse,
    setFulfillmentMethod,
};

export interface BranchIndicatorStyles {
    container?: InjectableCss;
    branchIndicatorWrapper?: InjectableCss;
    branchIndicatorHidden?: HiddenProps;
    branchIndicatorHiddenMobile?: HiddenProps;
    branchIndicatorIcon?: IconPresentationProps;
    branchIndicatorIconMobile?: IconPresentationProps;
    chevronIcon?: IconPresentationProps;
    chevronIconMobile?: IconPresentationProps;
    innerBranchIndicatorWrapper?: InjectableCss;
    salesCenterText?: TypographyPresentationProps;
    branchText?: TypographyPresentationProps;
    branchTextMobile?: TypographyPresentationProps;
    popover?: PopoverPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    warehousesContainer?: GridContainerProps;
    popoverTitleGridItem?: GridItemProps;
    popoverTitle?: TypographyPresentationProps;
    currentWarehouseRowGridItem?: GridItemProps;
    warehouseRowGridItem?: GridItemProps;
    warehouseInnerContainer?: GridContainerProps;
    warehouseInfoGridItem?: GridItemProps;
    warehouseButtonGridItem?: GridItemProps;
    warehouseTitle?: TypographyPresentationProps;
    warehouseAddressInfoCondensed?: AddressInfoCondensedDisplayStyles;
    warehousePhoneNumberText?: TypographyPresentationProps;
}

export const MOBILE_INDICATOR_HEIGHT = 40;

export const styles: BranchIndicatorStyles = {
    container: {
        css: css`
            display: inline-flex;
            height: 100%;
            margin: 0 0.5em;

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: [null, "0"], properties: ["margin"] },
                        { values: [null, Color(props.theme.colors.primary.main).string()], properties: ["background-color"] },
                        { values: [null, `${MOBILE_INDICATOR_HEIGHT}px`], properties: ["height"] },
                        { values: [null, "100%"], properties: ["width"] },
                        { values: [null, Color(props.theme.colors.primary.contrast).string()], properties: ["color"] },
                    ],
                    mediaOptions.max,
                )}

                ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        {
                            values: COMMON.spacing.side
                                .slice(0, 2)
                                .map(x => (x ? `calc(${x} - ${MENU.mobileButtonSpacing})` : x)),
                            properties: ["padding-right", "padding-left"],
                        },
                    ],
                    mediaOptions.minMax,
                )}
        `,
    },
    popover: {
        wrapperProps: {
            css: css`
                height: 100%;
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [{ values: ["static !important", null, null, "relative !important"], properties: ["position"] }], // Must be !important because it's not overridable
                        mediaOptions.min,
                    )}
            `,
        },
        contentBodyProps: {
            css: css`
                max-height: none;
                box-shadow: ${getProp("theme.shadows.main")};
                color: inherit;
                color: ${getColor("text")};
                margin-top: 1px;
            `,
        },
    },
    branchIndicatorWrapper: {
        css: css`
            color: ${getColor("header.contrastMain")};
            height: 100%;
            cursor: pointer;
            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        css`
                            padding: 0 ${MENU.mobileButtonSpacing};
                        `,
                    ],
                    mediaOptions.max,
                )}
        `,
    },
    branchIndicatorHidden: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: center;
        `,
    },
    branchIndicatorHiddenMobile: {
        css: css`
            display: flex;
            flex-direction: row;
            color: ${getColor("primary.contrast")};
            font-size: ${TYPOGRAPHY.size.smallest};
            align-items: center;
        `,
    },
    innerBranchIndicatorWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            margin-right: 0.5em;
        `,
    },
    salesCenterText: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.small};
            font-weight: ${TYPOGRAPHY.weight.bold};
            max-width: 160px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            margin-bottom: 0.25em;

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, TYPOGRAPHY.weight.base], properties: ["font-weight"] }],
                    mediaOptions.max,
                )}
        `,
    },
    branchIndicatorIcon: {
        color: "primary",
        size: 14,
        src: MapPin,
        css: css`
            margin-right: 0.25em;
        `,
    },
    branchIndicatorIconMobile: {
        size: 18,
        src: MapPin,
        css: css`
            color: ${getColor("primary.contrast")};
            margin: 0 0.5em 0.2em 0;
        `,
    },
    branchText: {
        css: css`
            display: flex;
            color: ${getColor("primary")};
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.bold};
            line-height: ${TYPOGRAPHY.lineHeight.small};
        `,
    },
    chevronIcon: {
        color: "primary",
        size: 15,
        src: ChevronDown,
    },
    chevronIconMobile: {
        color: "primary.contrast",
        size: 15,
        src: ChevronDown,
        css: css`
            margin: 0.25em 0 0 0.5em;
        `,
    },
    branchTextMobile: {
        css: css`
            color: ${getColor("primary.contrast")};
            font-weight: ${TYPOGRAPHY.weight.bold};
            white-space: pre;
        `,
    },
    loadingOverlay: getLoadingOverlayProps(),
    warehousesContainer: {
        gap: 0,
        css: css`
            padding: 20px;
        `,
    },
    warehouseRowGridItem: {
        width: 12,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 20px 0px 20px 20px;
        `,
    },
    popoverTitleGridItem: {
        width: 12,
        css: css`
            padding: 0px 20px 20px 0px;
        `,
    },
    popoverTitle: {
        weight: TYPOGRAPHY.weight.black,
        size: TYPOGRAPHY.size.large,
    },
    currentWarehouseRowGridItem: {
        width: 12,
        css: css`
            ${props => {
                return css`
                    background-color: ${Color(props.theme.colors.primary.main).lighten(0.85).hex()};
                `;
            }}
            padding: 20px 0px 20px 20px;
            flex-direction: column;
        `,
    },
    warehouseInnerContainer: {
        gap: 0,
    },
    warehouseInfoGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    warehouseButtonGridItem: {
        width: 6,
        css: css`
            align-items: flex-end;
            justify-content: flex-end;
        `,
    },
    warehouseTitle: {
        weight: TYPOGRAPHY.weight.bold,
    },
    warehouseAddressInfoCondensed: {
        addressText: {
            size: TYPOGRAPHY.size.smallest,

            css: css`
                color: ${getColor("text.disabled")};
            `,
        },
        cityStatePostalCodeText: {
            size: TYPOGRAPHY.size.smallest,

            css: css`
                color: ${getColor("text.disabled")};
            `,
        },
    },
    warehousePhoneNumberText: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
            color: ${getColor("text.disabled")};
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const BranchIndicator: React.FunctionComponent<Props> = ({
    isSignedIn,
    currentWarehouseName,
    currentWarehouseDescription,
    warehousesDataView,
    warehousesParameter,
    loadWarehouses,
    updatePickUpWarehouse,
    updateWarehouse,
    fulfillmentMethod,
    disallowedShippingBranches,
    setFulfillmentMethod,
}) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const isPickup = fulfillmentMethod === FulfillmentMethod.PickUp;

    React.useEffect(() => {
        loadWarehouses(warehousesParameter);
    }, []);

    const warehouses = warehousesDataView.value;
    const currentWarehouse = warehouses?.find(o => o.name === currentWarehouseName);
    const isCurrentWarehouseFound = !!currentWarehouse;
    const availableWarehouses = warehouses?.filter(warehouse => warehouse !== currentWarehouse);


    React.useEffect(() => {
        if (!warehousesDataView.value) {
            return;
        }

        if (!isCurrentWarehouseFound) {
            handleWarehouseSelected(warehousesDataView.value[0]);
        }
    }, [warehousesDataView.value]);

    const element = React.useRef<HTMLUListElement>(null);
    const popover = React.useRef<HTMLUListElement>(null);

    const handleWarehouseSelected = async (warehouse: WarehouseModel) => {
        setIsLoading(true);
        const disallowedBranchesArray = disallowedShippingBranches.split(',').map(branch => branch.trim());
        const isBranchPickupOnly = disallowedBranchesArray.includes(warehouse.name);
        const firstShippingAllowedWarehouse = warehousesDataView.value?.find(warehouse => !disallowedBranchesArray.includes(warehouse.name));
        const firstPickupAllowedWarehouse = warehousesDataView.value?.find(warehouse => warehouse.allowPickup);

        if (isPickup || isBranchPickupOnly) {
            await updatePickUpWarehouse({
                pickUpWarehouse: warehouse,
                onSuccess: () => {
                    updateWarehouse({
                        warehouse: isBranchPickupOnly ? firstShippingAllowedWarehouse || warehouse : warehouse,
                        onSuccess: () => {
                            setFulfillmentMethod({ fulfillmentMethod: FulfillmentMethod.PickUp });
                        },
                    });
                },
                onComplete: () => {
                    setIsLoading(false);
                },
            });
        } else {
            await updateWarehouse({
                warehouse,
                onSuccess: () => {
                    updatePickUpWarehouse({
                        pickUpWarehouse: !warehouse.allowPickup ? firstPickupAllowedWarehouse || warehouse : warehouse,
                    });
                },
                onComplete: () => {
                    setIsLoading(false);
                },
            });
        }
    };

    const branchIndicator = (
        <Clickable {...styles.branchIndicatorWrapper}>
            <Hidden {...styles.branchIndicatorHidden} below="md">
                <StyledWrapper {...styles.innerBranchIndicatorWrapper}>
                    <Typography {...styles.salesCenterText}>
                        {translate("Your Sales Center")}
                    </Typography>
                    <Typography {...styles.branchText}>
                        <Icon {...styles.branchIndicatorIcon} />
                        {currentWarehouseName} {currentWarehouseDescription}
                    </Typography>
                </StyledWrapper>
                <Icon {...styles.chevronIcon} />
            </Hidden>
            <Hidden {...styles.branchIndicatorHiddenMobile} above="sm">
                <Icon {...styles.branchIndicatorIconMobile} />
                {translate("Your Sales Center")} -
                <Typography {...styles.branchTextMobile}> {currentWarehouseName} {currentWarehouseDescription}</Typography>
                <Icon {...styles.chevronIconMobile} />
            </Hidden>
        </Clickable>
    );

    return (
        <StyledWrapper {...styles.container}>
            <Popover
                {...styles.popover}
                contentBodyProps={{
                    ...styles.popover?.contentBodyProps,
                    as: "div",
                    ref: element,
                    css: css`
                        ${styles.popover?.contentBodyProps?.css}
                        width: 100%;
                        left: 0;
                        height: 60vh;
                        ${props => {
                            if (isSignedIn) {
                                return breakpointValuesMediaQueries(
                                    props.theme,
                                    [
                                        { values: [null, null, "60%", "600px"], properties: ["width"] },
                                        { values: [null, null, "20%", "0"], properties: ["left"] },
                                        { values: [null, null, "650px"], properties: ["height"] },
                                    ],
                                    mediaOptions.min,
                                );
                            }

                            return breakpointValuesMediaQueries(
                                props.theme,
                                [{ values: [null, null, "285px"], properties: ["width"] }],
                                mediaOptions.min,
                            );
                        }}
                    `,
                }}
                positionFunction={() => {
                    return {
                        position: "absolute",
                    };
                }}
                ref={popover}
                insideRefs={[element]}
                popoverTrigger={branchIndicator}
            >
                <LoadingOverlay loading={isLoading} {...styles.loadingOverlay}>
                    <GridContainer {...styles.warehousesContainer}>
                        <GridItem {...styles.popoverTitleGridItem}>
                            <Typography {...styles.popoverTitle}>
                                {translate("Current Sales Center")}
                            </Typography>
                        </GridItem>
                        {currentWarehouse && (
                            <>
                                <GridItem {...styles.currentWarehouseRowGridItem}>
                                    <Typography {...styles.warehouseTitle}>
                                        {currentWarehouse.name} - {currentWarehouse.description}
                                    </Typography>
                                    <AddressInfoCondensedDisplay
                                        extendedStyles={styles.warehouseAddressInfoCondensed}
                                        {...currentWarehouse}
                                    />
                                    <Typography {...styles.warehousePhoneNumberText}>
                                        {currentWarehouse.phone}
                                    </Typography>
                                </GridItem>
                            </>
                        )}
                        {availableWarehouses?.map(warehouse => (
                            <GridItem {...styles.warehouseRowGridItem} key={warehouse.id}>
                                <GridContainer {...styles.warehouseInnerContainer}>
                                    <GridItem {...styles.warehouseInfoGridItem}>
                                        <Typography {...styles.warehouseTitle}>
                                            {warehouse.name} - {warehouse.description}
                                        </Typography>
                                        <AddressInfoCondensedDisplay
                                            extendedStyles={styles.warehouseAddressInfoCondensed}
                                            {...warehouse}
                                        />
                                        <Typography {...styles.warehousePhoneNumberText}>
                                            {warehouse.phone}
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.warehouseButtonGridItem}>
                                        <Button
                                            onClick={() => handleWarehouseSelected(warehouse)}
                                        >
                                            {translate("Set as Sales Center")}
                                        </Button>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        ))}
                    </GridContainer>
                </LoadingOverlay>
            </Popover>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(BranchIndicator)),
    definition: {
        group: "Common",
        displayName: "Branch Indicator",
        fieldDefinitions: [],
    },
};

export default widgetModule;
